import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import api from '@/api/axios';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useNoInternetDialog } from '@/hooks/useNoInternetDialog';
import { SetEditorFlow } from '@/contexts/SetEditorContext';
import { SET_DEFAULTS } from '@/contexts/SetEditorContext/constants';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { SetType } from '@/components/Template/types';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { handleTemplateCreationUtil } from '@/utils/createTemplate';
import { handleTemplateUpdateUtil } from '@/utils/updateTemplate';
import { fetchUpdatedPatientWithRetry } from '@/utils/fetchUpdatedPatientWithRetry';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';
import { logError } from '@/utils/logHandling';
import { useSetEditorCancel } from './useSetEditorCancel';
import { Patient } from '@/stores/store.types';

export function useSaveSet(flow: SetEditorFlow) {
  const navigation =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { checkInternet } = useNoInternetDialog();
  const { showNotification } = useNotification();
  const { formData, savePayload, resetFormData: resetEditor } = useSetEditor();
  const { patientsSyncing } = useDashboardContext();

  const exitToOrigin = useSetEditorCancel(flow, savePayload?.patientId);

  const save = useCallback(async () => {
    if (!savePayload) {
      return;
    }

    try {
      const hasInternet = await checkInternet();

      if (!hasInternet) {
        return;
      }

      const {
        apiKeyName,
        items,
        dataKeyName,
        dynamicName,
        patientId,
        setId,
        editData,
        fromTemplate,
      } = savePayload;
      const key = apiKeyName as SetType;
      const { name, appDisplayName, setType } = SET_DEFAULTS[key];

      // 1. Template update — update the existing template.
      if (flow === 'editTemplate' && fromTemplate) {
        const saved = await handleTemplateUpdateUtil({
          templateId: fromTemplate,
          name: formData.name,
          description: formData.description ?? '',
          difficulty: formData.difficulty!,
          items,
          onSuccess: () => exitToOrigin(),
          showNotification,
        });
        if (saved) {
          resetEditor();
        }
        return;
      }

      // 2. Template create (new, duplicated or derived from a set).
      if (
        flow === 'createTemplate' ||
        flow === 'templateFromSet' ||
        flow === 'duplicateTemplate'
      ) {
        const saved = await handleTemplateCreationUtil({
          name: formData.name,
          description: formData.description ?? '',
          difficulty: formData.difficulty!,
          setType,
          items,
          onSuccess: () => exitToOrigin(),
          showNotification,
        });
        if (saved) {
          resetEditor();
        }
        return;
      }

      const pid = patientId ?? '';
      const state = {
        name: formData.name || name,
        appDisplayName: formData.appDisplayName || appDisplayName,
      };

      const goToProfile = (patients: Patient[]) => {
        showNotification({
          type: 'success',
          title: 'Set erfolgreich gespeichert.',
        });
        navigation.navigate('PatientProfile', {
          id: pid,
          patients: patients as never,
          replacedTitle: replacedPatientProfileHeader({
            patients,
            patientId: pid,
            patient: undefined,
          }),
        });
        resetEditor();
      };

      // 3. Set update — update the existing set.
      if (flow === 'editSet' && setId) {
        const bodyData = {
          ...editData,
          [`${dynamicName}Items`]: [],
          ...state,
          updatedAt: new Date().toISOString(),
          patientId: pid,
          [dataKeyName]: items,
        };
        await api.put(
          `/api/patients/${pid}/${apiKeyName}/${setId}?api_version=v2`,
          bodyData,
        );
        const { patients } = await fetchUpdatedPatientWithRetry(
          pid,
          key,
          setId,
        );
        await patientsSyncing(patients);
        goToProfile(patients);
        return;
      }

      // 4. Set create — create a new set.
      const bodyData = {
        ...state,
        patientId: pid,
        [dataKeyName]: items,
      };
      const { data } = await api.post(
        `/api/patients/${pid}/${apiKeyName}?api_version=v2`,
        bodyData,
      );
      const { patients } = await fetchUpdatedPatientWithRetry(
        pid,
        key,
        data._id,
      );
      goToProfile(patients);
    } catch (error) {
      showNotification({
        type: 'error',
        title: 'Fehler beim Speichern des Sets.',
      });
      logError('Error saving set', error);
    }
  }, [
    checkInternet,
    savePayload,
    formData,
    resetEditor,
    showNotification,
    navigation,
    flow,
    exitToOrigin,
    patientsSyncing,
  ]);

  return { save };
}
